import { useCallback, useEffect, useState } from "react";

/**
 * In-session theme persistence.
 *
 * We deliberately avoid localStorage: the rest of the app keeps preferences
 * either in the URL hash (community selector) or in memory. This hook follows
 * the same model — the user's explicit choice survives navigation but resets
 * on a full reload, falling back to the OS `prefers-color-scheme` setting.
 *
 * Returns:
 *  - `resolved` — the theme actually applied right now ("light" | "dark").
 *  - `override` — null when we're tracking the OS preference, otherwise the
 *    user's manual choice.
 *  - `toggle()` — flip to the opposite of whatever is resolved.
 */
export type ThemeName = "light" | "dark";

function getSystemTheme(): ThemeName {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function useTheme(): {
  resolved: ThemeName;
  override: ThemeName | null;
  toggle: () => void;
} {
  const [override, setOverride] = useState<ThemeName | null>(null);
  const [system, setSystem] = useState<ThemeName>(getSystemTheme);

  // Watch OS changes so the resolved theme updates live when override is null.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setSystem(e.matches ? "dark" : "light");
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Mirror current resolved theme onto <html data-theme> so CSS can react.
  // When override is null we remove the attribute, so the CSS media query
  // takes effect and the OS preference wins (matches color-scheme too).
  const resolved: ThemeName = override ?? system;
  useEffect(() => {
    const html = document.documentElement;
    if (override === null) {
      html.removeAttribute("data-theme");
    } else {
      html.setAttribute("data-theme", override);
    }
  }, [override]);

  const toggle = useCallback(() => {
    setOverride((curr) => {
      const current = curr ?? system;
      return current === "dark" ? "light" : "dark";
    });
  }, [system]);

  return { resolved, override, toggle };
}
